import {AGE_GROUPS, DEFAULT_LATITUDE, DEFAULT_LONGITUDE} from '@/constants';
import {
  initialState,
  resetEventFormData,
  setEventFormData,
  updateEventInRedux
} from '@/redux-store/slices/events';
import {addEventToGroup} from '@/redux-store/slices/groups';
import {addEvent} from '@/rest/events/add-event';
import {
  EventData,
  EventFormData,
  EventFormMode,
  SportEvent
} from '@/types/event';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  isSameDay,
  addDays,
  startOfToday,
  addMinutes,
  setHours,
  setMinutes,
  parseISO,
  getDay,
  differenceInWeeks,
  addHours,
  addYears
} from 'date-fns';
import {store, useAppSelector} from '@/redux-store/store';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import EventValidationSchema, {
  DESC_MAX_LENGTH,
  TITLE_MAX_LENGTH
} from '@/schemas/events';
import {
  calculateBrutto,
  calculateNetto,
  handleOnboarding
} from '@/services/payments';
import {Location} from '@/schemas/sub-schemas';
import ConfirmationDialog from '@/components/ui/dialogs/confirmation-dialog';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {TimePicker} from '@mui/x-date-pickers/TimePicker';
import {
  FormControlLabel,
  FormControl,
  Switch,
  TextField,
  Select,
  MenuItem,
  Grid,
  Button,
  InputLabel,
  FormHelperText,
  Box,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Link,
  Tooltip,
  IconButton
} from '@mui/material';
import {Formik, Form, FormikProps} from 'formik';
import TypographyTitle from '@/components/ui/typography-title';
import {LocationInput} from '../input/location';
import {SportSelectInput} from '../input/sports';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import CachedIcon from '@mui/icons-material/Cached';
import {updateEvent} from '@/rest/events/update-event';
import {Sport} from '@/types/common';
import {defaultLocation} from '@/schemas/sub-schemas';
import {createAccount} from '@/rest/payments/create-account';
import {useSnackbar} from '@/contexts/snackbar';
import {getAccount} from '@/rest/payments/get-account';
import {env} from '@/config';
import {CompanyInput} from '../input/company';
import {
  decodeBase64ToObject,
  getAppTag,
  isAllowedTag,
  isHealthManager,
  isUserDocumentManager
} from '@/utils';
import {AxiosError} from 'axios';
import OdooCompanyForm from '../odoo-company';
import DialogForm from '@/components/ui/dialogs/form-dialog';
import FormLabel from '@/components/ui/form-label';
import ResponsiveTypography from '@/components/ui/responsive-typography';
import {setPaymentFeeData} from '@/redux-store/slices/auth';
import {getPaymentFee} from '@/rest/payments/get-payment-fee';
import {getCompanyTax} from '@/rest/payments/get-company-tax';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  resetOnDemandEventFormData,
  setOnDemandEventFormData
} from '@/redux-store/slices/on-demand-events';
import i18n from '@/i18n/i18n';

type OdooErrorResponse = {
  error?:
    | string
    | {
        data?: {
          arguments?: string[];
        };
      };
};

type EventFormDataLocal = {
  recurring: number[];
  end: string | null;
  time: string | null;
  endTime: string | null;
  title: string;
  online: boolean;
  description: string;
  accessType: string;
  locationName: string;
  coordinates: number[];
  sports: string | string[];
  ageGroups: string[];
  group: string;
  maxParticipants: number | null;
  company: string;
  isPaid: boolean;
  price: number;
  imprintLink: string;
  termsOfUseLink: string;
  cancellationPolicyLink: string;
  trainerEvent: boolean;
  myGroup: string;
  picture: string;
  location: Location | null;
};

const {APP_VARIANT, TAG_PAIRS, TERMS_OF_USE_URL, CONTENT_SERVER} = env;

function getMaximumDate(date: string | null) {
  let dateToUse: Date;
  if (date) {
    dateToUse = parseISO(date);
  } else {
    dateToUse = new Date();
  }

  const inYear = addYears(dateToUse, 1);
  return inYear;
}

function resetTimeToMidnight(date: Date): Date {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
const processSports = (response: any) => {
  const modifiedResponse = {...response};
  modifiedResponse.sports = modifiedResponse.sports.map(
    (sportEvent: SportEvent) => ({
      ...sportEvent,
      _id: sportEvent.sport._id,
      sport: sportEvent.sport._id
    })
  );
  return modifiedResponse;
};

interface EventFormProps {
  mode: EventFormMode;
  onCancel?: () => void;
  onSubmit?: (newEvent: EventFormData) => void;
  onError?: (error: Error) => void;
  defaultGroup?: string;
  groupOfEventChanged?: () => void;
  initialValues?: EventData;
  eventId?: string;
  isVerified?: boolean;
  onDemand?: boolean;
}

const EventForm: React.FC<EventFormProps> = ({
  mode,
  onCancel,
  onSubmit,
  onError,
  defaultGroup,
  groupOfEventChanged,
  initialValues,
  eventId,
  isVerified,
  onDemand = false
}) => {
  const userRole = useAppSelector((state) => state.auth.keycloakUser?.userRole);
  const valuesFromStateEvents = useAppSelector(
    (state) => state.events.eventForm
  );
  const valuesFromStateOnDemandEvents = useAppSelector(
    (state) => state.onDemandEvents.onDemandEventForm
  );
  const valuesFromStateToUse = onDemand
    ? valuesFromStateOnDemandEvents
    : valuesFromStateEvents;
  const myGroups = useAppSelector((state) => state.groups.myGroups);
  const userData = useAppSelector((state) => state.userInfos.userInfos);
  const sportsFromRedux = useAppSelector((state) => state.sports);
  const {
    stripeAccountVerified,
    isUserHealthManager,
    paymentFeeData,
    uses19Vat
  } = useAppSelector((state) => state.auth);

  const tagPairs = decodeBase64ToObject(TAG_PAIRS);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formikRef = useRef<FormikProps<any>>(null);

  const availableCompanies = userData.companies;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getErrorMessage = (error: any): string | string[] | undefined => {
    if (typeof error === 'string') {
      return error;
    }
    if (Array.isArray(error)) {
      return error.every((item) => typeof item === 'string')
        ? error
        : undefined;
    }
    return undefined;
  };

  const trainerInSjOne = userRole === 'TRAINER' && APP_VARIANT === 'sjone';

  const companyId =
    APP_VARIANT === 'sjone' &&
    userData &&
    userData.companies &&
    userData.companies.length === 1 &&
    userData.companies[0].id._id;
  const myAuthorizedGroups = myGroups.filter((group) => {
    return (
      isUserDocumentManager(group, userData) ||
      group.members.some((member) => member._id === userData._id) ||
      (group.companyRole &&
        group.companyRole === 'HEALTH_MANAGER' &&
        isUserHealthManager)
    );
  });

  const [titleLength, setTitleLength] = useState(
    valuesFromStateToUse.title.length
  );
  const [descriptionLength, setDescriptionLength] = useState(
    valuesFromStateToUse.description.length
  );
  const [stripeVerificationDialogVisible, setStripeVerificationDialogVisible] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [circularProgressVisible, setCircularProgressVisible] = useState(false);
  const [odooCompanyFormDialogOpen, setOdooCompanyFormDialogOpen] =
    useState(false);
  const [odooCompanyCreated, setOdooCompanyCreated] = useState(false);
  const [togglePaidSwitched, setTogglePaidSwitched] = useState(false);
  const [fee, setFee] = useState<string | null>(null);
  const [bruttoPrice, setBruttoPrice] = useState('0');
  const [fileSizeError, setFileSizeError] = useState<string | null>(null);

  const eventImage = `${CONTENT_SERVER}assets/sports/team-new.jpg`;

  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const tag = getAppTag(userData.email || '', tagPairs);
  const allowedTag = tag && isAllowedTag(tag, TAG_PAIRS) ? tag : undefined;

  const {t} = useTranslation();

  const {showSnackbarMessage} = useSnackbar();

  const currentParticipants =
    initialValues && initialValues.participants.length;

  const effectiveCurrentParticipants = currentParticipants ?? 1;

  const eventValidationSchema = EventValidationSchema(
    t,
    currentParticipants || null,
    trainerInSjOne,
    parseFloat(bruttoPrice),
    onDemand
  );

  const isAllowedToCreatePaidEvents =
    APP_VARIANT === 'sportjoe' ||
    (APP_VARIANT === 'sjone' &&
      (userRole === 'TRAINER' ||
        (userData.companies &&
          userData.companies.length === 1 &&
          (isHealthManager(userData.companies[0].role) ||
            userRole === 'ADMIN'))));

  const dispatch = useDispatch();

  const today = startOfToday();

  const handleFieldChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (field: string, value: any) => {
      const action = onDemand ? setOnDemandEventFormData : setEventFormData;
      dispatch(action({[field]: value}));
    },
    [dispatch, onDemand]
  );

  const handleResetState = useCallback(() => {
    if (onDemand) {
      dispatch(resetOnDemandEventFormData());
    } else dispatch(resetEventFormData());
  }, [dispatch, onDemand]);

  const resetTextLength = useCallback(() => {
    setTitleLength(0);
    setDescriptionLength(0);
  }, []);

  const handleLocationSelected = useCallback(
    (location: Location) => {
      dispatch(setEventFormData({location}));
    },
    [dispatch]
  );

  const isGroupCompany = useCallback(
    (id: string) => {
      const group = myAuthorizedGroups.find((g) => g._id === id);
      if (group && group.company) {
        return true;
      }
      return false;
    },
    [myAuthorizedGroups]
  );

  const getGroupCompanyId = useCallback(
    (groupId?: string) => {
      if (trainerInSjOne && groupId) {
        const groupCompany =
          myAuthorizedGroups.find((g) => g._id === groupId)?.company ??
          undefined;

        if (groupCompany) {
          const company = availableCompanies?.find(
            (c) => c.id._id === groupCompany
          );
          return company?.id._id;
        }
        return undefined;
      }
      return undefined;
    },
    [availableCompanies, myAuthorizedGroups, trainerInSjOne]
  );

  const handleStripeDialogClose = useCallback(() => {
    setStripeVerificationDialogVisible(false);
  }, []);

  const onOdooCompanyCreated = useCallback(() => {
    showSnackbarMessage(t('company_creation_succeeded'), 'success', 6000);
    setOdooCompanyFormDialogOpen(false);
    if (stripeAccountVerified && formikRef.current) {
      formikRef.current.setFieldValue('isPaid', true);
      formikRef.current.setFieldTouched('isPaid', true);
      handleFieldChange('isPaid', true);
    }
  }, [showSnackbarMessage, t, stripeAccountVerified, handleFieldChange]);

  const handleOpenOdooCompanyForm = useCallback(() => {
    setOdooCompanyFormDialogOpen(true);
  }, []);

  const onOdooCompanyCreationError = useCallback(
    (error?: Error) => {
      const axiosError = error as AxiosError<OdooErrorResponse>;

      const odooError = axiosError.response?.data?.error;

      if (
        typeof odooError === 'object' &&
        (odooError?.data?.arguments?.[0]?.includes('name must be unique') ||
          odooError?.data?.arguments?.[0]?.includes('is already linked with'))
      ) {
        showSnackbarMessage(t('company_name_already_exists'), 'error', 6000);
        return;
      }
      if (
        typeof odooError === 'string' &&
        odooError.includes('This email is already associated with another user')
      ) {
        showSnackbarMessage(t('company_mail_already_exists'), 'error', 6000);
        return;
      }
      showSnackbarMessage(t('company_creation_failed_support'), 'error', 6000);
    },
    [showSnackbarMessage, t]
  );

  const onOdooCompanyCreationCanceled = useCallback(() => {
    showSnackbarMessage(t('company_creation_failed'), 'error', 6000);
    setOdooCompanyFormDialogOpen(false);
  }, [showSnackbarMessage, t]);

  const handleStripeOnboarding = useCallback(async () => {
    handleStripeDialogClose();
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    setTogglePaidSwitched(true);
    try {
      if (!odooCompanyCreated) {
        handleOpenOdooCompanyForm();
        setIsLoading(false);
        return;
      }

      if (odooCompanyCreated) {
        const eventId =
          mode === EventFormMode.UPDATE && initialValues
            ? initialValues._id
            : undefined;

        showSnackbarMessage(t('stripe_onboarding_info'), 'info', 12000);
        const accountInfo = await getAccount();
        if (!accountInfo) {
          await createAccount(userData._id);
        }
        setCircularProgressVisible(true);
        await handleOnboarding(mode, eventId);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [
    handleStripeDialogClose,
    isLoading,
    odooCompanyCreated,
    handleOpenOdooCompanyForm,
    mode,
    initialValues,
    showSnackbarMessage,
    t,
    userData
  ]);

  const recurringEventsCount = useMemo(() => {
    if (
      valuesFromStateEvents &&
      valuesFromStateEvents.isPaid &&
      valuesFromStateEvents.recurring &&
      valuesFromStateEvents.date &&
      valuesFromStateEvents.end &&
      !onDemand
    ) {
      const {date, end} = valuesFromStateEvents;
      return differenceInWeeks(new Date(end), new Date(date)) + 1;
    }
    return 1;
  }, [onDemand, valuesFromStateEvents]);

  const onlineEventLocation = {
    lat: -52,
    lng: -137,
    label: 'Online Event'
  };

  const handleFormSubmit = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (values: any) => {
      let combinedDateTime, combinedEndDateTime, dayOfWeek;
      if (onDemand) {
        const fixedDate = new Date(2099, 11, 31);
        combinedDateTime = setMinutes(setHours(fixedDate, 10), 0);
        combinedEndDateTime = setMinutes(setHours(fixedDate, 11), 0);
      } else {
        const time = new Date(values.time);
        const hours = time.getHours();
        const minutes = time.getMinutes();

        const date = parseISO(values.date);
        combinedDateTime = setMinutes(setHours(date, hours), minutes);
        dayOfWeek = getDay(parseISO(values.date)) + 1;

        const endTime = new Date(values.endTime);
        const endTimeHours = endTime.getHours();
        const endTimeMinutes = endTime.getMinutes();

        const endDate = parseISO(values.end);

        combinedEndDateTime = setMinutes(
          setHours(endDate, endTimeHours),
          endTimeMinutes
        );
      }

      const {
        recurring,
        title,
        online,
        description,
        accessType,
        location,
        sports,
        ageGroups,
        maxParticipants,
        isPaid,
        currency,
        imprintLink,
        termsOfUseLink,
        cancellationPolicyLink,
        company,
        onDemandLink
      } = values;

      const baseEventObject = {
        start: combinedDateTime,
        end: combinedEndDateTime,
        recurring:
          recurring && dayOfWeek && recurring[0] === 0 ? [dayOfWeek] : [],
        title: title,
        picture: valuesFromStateEvents.picture || undefined,
        description: description,
        online: online,
        locationName: online ? onlineEventLocation.label : location.label,
        coordinates: [
          online ? onlineEventLocation.lng : location.lng || DEFAULT_LONGITUDE,
          online ? onlineEventLocation.lat : location.lat || DEFAULT_LATITUDE
        ],
        sports: [{sport: sports}],
        ageGroups: ageGroups.includes('ALL') ? null : ageGroups,
        accessType: accessType,
        maxParticipants: maxParticipants,
        company: companyId || company || undefined,
        isPaid: isPaid,
        price: isPaid ? parseFloat(bruttoPrice) : 0,
        imprintLink: isPaid ? imprintLink : undefined,
        termsOfUseLink: isPaid ? termsOfUseLink : undefined,
        cancellationPolicyLink: isPaid ? cancellationPolicyLink : undefined,
        currency: currency,
        tag: allowedTag,
        trainerEvent: userRole === 'TRAINER' ? true : false,
        onDemand: onDemand,
        onDemandLink: onDemandLink
      };

      const eventObject = values.myGroup
        ? {...baseEventObject, group: values.myGroup}
        : baseEventObject;

      if (mode === EventFormMode.CREATE || mode === EventFormMode.CLONE) {
        addEvent(eventObject)
          .then((response) => {
            if (response) {
              const events = Array.isArray(response) ? response : [response];
              events.forEach((event) => {
                const modifiedEvent = processSports(event);
                if (values.myGroup) {
                  dispatch(
                    addEventToGroup({
                      groupId: values.myGroup,
                      event: modifiedEvent
                    })
                  );
                }
                if (onSubmit) {
                  onSubmit(event);
                }
              });
              handleResetState();
            } else {
              if (onError) {
                onError(new Error('Error creating new event(s)'));
              }
            }
          })
          .catch((error) => {
            console.error('Error:', error);
            if (onError) {
              onError(error as Error);
            }
          });
      } else if ((mode === EventFormMode.UPDATE && eventId) || values._id) {
        updateEvent(values._id, eventObject)
          .then((response) => {
            if (response) {
              const newResponse = {...response};

              const modifiedResponse = processSports(newResponse);

              dispatch(
                updateEventInRedux({
                  event: modifiedResponse
                })
              );
              if (onSubmit) {
                onSubmit(response);
                handleResetState();
                if (groupOfEventChanged) {
                  groupOfEventChanged();
                }
              }
            } else {
              if (onError) {
                onError(new Error('Error updating the event'));
              }
            }
          })
          .catch((error) => {
            console.error('Error:', error);
            if (onError) {
              onError(error as Error);
            }
          });
      }
    },
    [
      onlineEventLocation.label,
      onlineEventLocation.lng,
      onlineEventLocation.lat,
      companyId,
      userRole,
      mode,
      eventId,
      dispatch,
      onSubmit,
      onError,
      groupOfEventChanged,
      bruttoPrice,
      onDemand,
      handleResetState,
      allowedTag,
      valuesFromStateEvents.picture
    ]
  );

  const computedInitialStateForCreate: Partial<EventFormDataLocal> = {
    ...valuesFromStateToUse,
    ...(onDemand
      ? {
          onDemandLink: valuesFromStateOnDemandEvents.onDemandLink,
          location: onlineEventLocation
        }
      : {
          end: valuesFromStateEvents.end ? valuesFromStateEvents.end : null,
          ageGroups: Array.isArray(valuesFromStateEvents.ageGroups)
            ? valuesFromStateEvents.ageGroups.length
              ? valuesFromStateEvents.ageGroups
              : ['ALL']
            : [valuesFromStateEvents.ageGroups || 'ALL'],
          myGroup: onDemand
            ? undefined
            : defaultGroup || valuesFromStateEvents.myGroup,
          isPaid: isVerified ? true : valuesFromStateEvents.isPaid,
          online: onDemand ? true : valuesFromStateEvents.online,
          company:
            getGroupCompanyId(defaultGroup || valuesFromStateEvents.myGroup) ||
            valuesFromStateEvents.company ||
            undefined
        })
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const computedInitialStateForUpdateAndClone = initialValues
    ? {
        ...initialValues,
        accessType: initialValues.accessType || 'PUBLIC',
        date:
          mode === EventFormMode.CLONE
            ? null
            : resetTimeToMidnight(initialValues.start).toISOString(),
        time: mode === EventFormMode.CLONE ? null : initialValues.start,
        end: mode === EventFormMode.CLONE ? null : initialValues.end,
        endTime: mode === EventFormMode.CLONE ? null : initialValues.end,
        recurring: onDemand
          ? undefined
          : mode === EventFormMode.CLONE
          ? []
          : initialValues.recurring,
        location: onDemand
          ? onlineEventLocation
          : {
              lat:
                initialValues.geolocation.coordinates[1] || defaultLocation.lat,
              lng:
                initialValues.geolocation.coordinates[0] || defaultLocation.lng,
              label: initialValues.locationName || defaultLocation.label
            },
        locationName: onDemand
          ? undefined
          : initialValues.online
          ? onlineEventLocation.label
          : initialValues.locationName,
        online: onDemand
          ? true
          : initialValues.online === undefined
          ? false
          : initialValues.online,
        ageGroups: Array.isArray(initialValues.ageGroups)
          ? initialValues.ageGroups.length
            ? initialValues.ageGroups
            : ['ALL']
          : [initialValues.ageGroups || 'ALL'],
        sports: initialValues.sports
          .map((sportObject: {sport: string | Sport; _id: string}) =>
            typeof sportObject.sport === 'string'
              ? sportObject.sport
              : sportObject.sport._id
          )
          .join(','),
        isPaid: isVerified ? true : initialValues.isPaid,
        myGroup: initialValues.group ? initialValues.group._id : null,
        maxParticipants: initialValues.maxParticipants
          ? initialValues.maxParticipants
          : null,
        company:
          getGroupCompanyId(defaultGroup || valuesFromStateEvents.myGroup) ||
          initialValues.company ||
          undefined,
        price:
          initialValues.isPaid && uses19Vat !== null
            ? calculateNetto(initialValues.price, uses19Vat)
            : 0,
        onDemandLink: initialValues.onDemandLink || undefined
      }
    : computedInitialStateForCreate;

  const ageGroupsAll = AGE_GROUPS.map((group) => ({
    value: group.value,
    label:
      group.value === 'ALL'
        ? t(group.labelKey)
        : `${t(group.labelKey)} (${group.ageRange} ${t('year_abbr')})`
  }));

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const fileSize = file.size / 1024 / 1024;
      if (fileSize > 0.5) {
        setFileSizeError(t('file_size_error', {maxSize: 500, unit: 'KB'}));

        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(event.target.files[0]);
      reader.onload = () => {
        dispatch(
          setEventFormData({
            picture: reader.result as string
          })
        );
        setFileSizeError(null);
      };
    }
  };

  const pictureSource = useMemo(() => {
    if (!valuesFromStateEvents) return eventImage || null;

    if (valuesFromStateEvents.picture) {
      return valuesFromStateEvents.picture;
    }

    const sports = valuesFromStateEvents.sports;
    if (Array.isArray(sports) && sports.length === 1) {
      const sportId = sports[0];
      return sportsFromRedux.sports?.[sportId]?.picture ?? null;
    }
    if (sports && typeof sports === 'string') {
      return sportsFromRedux.sports?.[sports]?.picture ?? null;
    }

    return eventImage || null;
  }, [sportsFromRedux.sports, eventImage, valuesFromStateEvents]);

  const handleDeleteImage = useCallback(() => {
    dispatch(
      setEventFormData({
        picture: undefined
      })
    );
    setFileSizeError(null);
  }, [dispatch]);

  useEffect(() => {
    if (mode === EventFormMode.UPDATE || mode === EventFormMode.CLONE) {
      if (computedInitialStateForUpdateAndClone) {
        const transformedState = {
          ...computedInitialStateForUpdateAndClone,
          time: computedInitialStateForUpdateAndClone.time
            ? new Date(computedInitialStateForUpdateAndClone.time).toISOString()
            : null,
          end: computedInitialStateForUpdateAndClone.end
            ? new Date(computedInitialStateForUpdateAndClone.end).toISOString()
            : null,
          endTime: computedInitialStateForUpdateAndClone.endTime
            ? new Date(
                computedInitialStateForUpdateAndClone.endTime
              ).toISOString()
            : null,
          myGroup: computedInitialStateForUpdateAndClone.myGroup ?? undefined
        };

        dispatch(setEventFormData(transformedState));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!odooCompanyCreated) {
      const isCompanyCreated = store.getState().userInfos.userInfos.odooCompany;
      if (isCompanyCreated) {
        setOdooCompanyCreated(true);
      }
    }
  }, [handleStripeOnboarding, odooCompanyCreated, stripeAccountVerified]);

  useEffect(() => {
    if (
      odooCompanyCreated &&
      !stripeAccountVerified &&
      !isLoading &&
      togglePaidSwitched
    ) {
      handleStripeOnboarding();
      setTogglePaidSwitched(false);
    }
  }, [
    handleStripeOnboarding,
    isLoading,
    odooCompanyCreated,
    stripeAccountVerified,
    togglePaidSwitched
  ]);

  useEffect(() => {
    if (
      valuesFromStateToUse.isPaid &&
      valuesFromStateToUse.price &&
      valuesFromStateToUse.price > 0
    ) {
      const usesVat = uses19Vat;
      if (!usesVat) {
        setBruttoPrice(valuesFromStateToUse.price.toFixed(2));
      } else {
        const brutto = calculateBrutto(valuesFromStateToUse.price, usesVat);
        setBruttoPrice(brutto.toFixed(2));
      }
    }
  }, [uses19Vat, valuesFromStateToUse.isPaid, valuesFromStateToUse.price]);

  useEffect(() => {
    if (paymentFeeData) {
      const {fixedFee, variableRate, isStandardFee} = paymentFeeData;
      let feeSuffix;
      let locale: string = 'de-DE';
      const currentLanguage = i18n.language || 'de';
      if (currentLanguage === 'en') {
        locale = 'en-EN';
      }
      const euroSymbol = currentLanguage === 'de' ? '€ ' : '€';
      const formattedVariableRate = (variableRate * 100).toLocaleString(
        locale,
        {minimumFractionDigits: 1}
      );
      const formattedFixedFee = fixedFee.toLocaleString(locale, {
        minimumFractionDigits: 2
      });
      if (fixedFee > 0 && variableRate > 0) {
        feeSuffix = `${formattedVariableRate}% + ${euroSymbol}${formattedFixedFee}`;
      } else if (variableRate > 0 && fixedFee === 0) {
        feeSuffix = `${formattedVariableRate}%`;
      } else if (variableRate === 0 && fixedFee > 0) {
        feeSuffix = `${euroSymbol}${formattedFixedFee}`;
      } else {
        feeSuffix = `${euroSymbol}0`;
      }

      const finalFee = `${t('fee')} ${
        !isStandardFee ? t('as_agreed') : ''
      } ${feeSuffix}`;

      setFee(finalFee);
    }
  }, [paymentFeeData, t]);

  useEffect(() => {
    const checkAndFetchFeeData = async () => {
      try {
        const paymentFeeData = await getPaymentFee();
        if (paymentFeeData) {
          dispatch(setPaymentFeeData(paymentFeeData));
        }
      } catch (error) {
        console.error(
          'Error during verification or fetching payment fee data:',
          error
        );
      }
    };

    checkAndFetchFeeData();
  }, [dispatch, mode]);

  return (
    <>
      <div style={{display: odooCompanyFormDialogOpen ? 'none' : 'block'}}>
        <Formik
          innerRef={formikRef}
          initialValues={
            mode === EventFormMode.CREATE
              ? computedInitialStateForCreate
              : computedInitialStateForUpdateAndClone
          }
          validationSchema={eventValidationSchema}
          validateOnMount={mode === EventFormMode.CREATE ? false : true}
          validateOnBlur={true}
          validateOnChange={true}
          onSubmit={(values) => {
            handleFormSubmit(values);
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            resetForm,
            setFieldTouched,
            validateForm
          }) => {
            //console.log('errors: ', errors);
            //console.log('touched: ', touched);
            //console.log('values: ', values);
            const dateToUse = values.date ? new Date(values.date) : today;
            const inWeek = addDays(dateToUse, 7);
            const halfHourFromStart =
              values.date &&
              values.time &&
              values.end &&
              isSameDay(parseISO(values.date), parseISO(values.end))
                ? addMinutes(parseISO(values.time), 30)
                : undefined;

            const locationError = Array.isArray(errors['location'])
              ? undefined
              : errors['location'];

            const sportsError = Array.isArray(errors.sports)
              ? errors.sports.flatMap((err) =>
                  typeof err === 'string' ? err : []
                )
              : typeof errors.sports === 'string'
              ? errors.sports
              : undefined;

            const handleToggleIsPaid = async (value: boolean) => {
              if (value && (!stripeAccountVerified || !odooCompanyCreated)) {
                setStripeVerificationDialogVisible(true);
                handleFieldChange('isPaid', false);
                setFieldValue('isPaid', false);
                return;
              }
              if (uses19Vat === null) {
                const companyMail = userData.odooCompany;
                if (companyMail) {
                  await getCompanyTax(companyMail);
                } else {
                  return;
                }
              }
              handleFieldChange('isPaid', value);
              setFieldValue('isPaid', value);
            };

            return (
              <Form>
                <TypographyTitle textKey="new_event" />
                {myAuthorizedGroups.length > 0 && !onDemand && (
                  <Grid container spacing={2} marginTop={1} marginBottom={3}>
                    <Grid item xs={12}>
                      <FormControl
                        sx={{width: '100%'}}
                        variant="outlined"
                        fullWidth
                      >
                        <InputLabel
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          <FormLabel text="select_group" />
                        </InputLabel>
                        <Select
                          name="myGroup"
                          value={values.myGroup || ''}
                          onChange={(e) => {
                            handleChange(e);
                            handleFieldChange(
                              'myGroup',
                              e.target.value as string
                            );

                            const companyId = getGroupCompanyId(
                              e.target.value as string
                            );
                            if (companyId && trainerInSjOne) {
                              handleFieldChange('company', companyId);
                              setFieldValue('company', companyId);
                            }
                          }}
                          onBlur={handleBlur}
                          error={touched.myGroup && Boolean(errors.myGroup)}
                          label={t('select_group')}
                        >
                          <MenuItem value="">
                            <em>{t('no_group')}</em>
                          </MenuItem>

                          {myAuthorizedGroups.map((group) => (
                            <MenuItem key={group._id} value={group._id}>
                              {group.title}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                )}
                {trainerInSjOne &&
                  availableCompanies &&
                  availableCompanies.length > 0 && (
                    <Grid container spacing={2} marginTop={1} marginBottom={3}>
                      <Grid item xs={12}>
                        <CompanyInput
                          companies={availableCompanies}
                          disabled={
                            !!(values.myGroup && isGroupCompany(values.myGroup))
                          }
                          value={
                            availableCompanies.find(
                              (company) => company.id._id === values.company
                            ) || null
                          }
                          onChange={(_, newValue) => {
                            if (newValue && !Array.isArray(newValue)) {
                              handleFieldChange(
                                'company',
                                newValue.id._id || undefined
                              );
                              setFieldValue(
                                'company',
                                newValue.id._id || undefined
                              );
                            }
                          }}
                          error={getErrorMessage(errors.company)}
                          touched={!!touched.company}
                          onBlur={handleBlur}
                          label={t('company')}
                        />
                      </Grid>
                    </Grid>
                  )}
                <Grid container spacing={2} my={1}>
                  <Grid item xs={12}>
                    <TextField
                      name="title"
                      label={t('event_prop_title')}
                      value={values.title}
                      onChange={(e) => {
                        handleChange(e);
                        setTitleLength(e.target.value.length);
                        handleFieldChange('title', e.target.value);
                      }}
                      onBlur={handleBlur}
                      error={touched.title && Boolean(errors.title)}
                      helperText={
                        touched.title && errors.title
                          ? typeof errors.title === 'string'
                            ? errors.title
                            : JSON.stringify(errors.title)
                          : ''
                      }
                      fullWidth
                    />
                    <FormHelperText>
                      {titleLength} / {TITLE_MAX_LENGTH}
                    </FormHelperText>
                  </Grid>
                </Grid>
                <Grid container spacing={2} sx={{my: {xs: 0, sm: 1}}}>
                  <Grid item xs={12}>
                    <TextField
                      name="description"
                      label={t('description')}
                      value={values.description}
                      onChange={(e) => {
                        handleChange(e);
                        setDescriptionLength(e.target.value.length);
                        handleFieldChange('description', e.target.value);
                      }}
                      onBlur={handleBlur}
                      error={touched.description && Boolean(errors.description)}
                      helperText={
                        touched.description && errors.description
                          ? typeof errors.description === 'string'
                            ? errors.description
                            : JSON.stringify(errors.description)
                          : ''
                      }
                      fullWidth
                      multiline
                      maxRows={4}
                    />
                    <FormHelperText>
                      {descriptionLength} / {DESC_MAX_LENGTH}
                    </FormHelperText>
                  </Grid>
                </Grid>
                {onDemand && (
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      marginTop: {xs: 0, sm: 1},
                      marginBottom: {xs: 1, sm: 2}
                    }}
                  >
                    <Grid item xs={12}>
                      <TextField
                        name="onDemandLink"
                        label={t('on_demand_link')}
                        value={values.onDemandLink}
                        onChange={(e) => {
                          handleChange(e);
                          handleFieldChange('onDemandLink', e.target.value);
                        }}
                        fullWidth
                        onBlur={handleBlur}
                        error={
                          touched.onDemandLink && Boolean(errors.onDemandLink)
                        }
                        helperText={
                          touched.onDemandLink && errors.onDemandLink
                            ? typeof errors.onDemandLink === 'string'
                              ? errors.onDemandLink
                              : JSON.stringify(errors.onDemandLink)
                            : ''
                        }
                      />
                    </Grid>
                  </Grid>
                )}
                {!onDemand && (
                  <Grid container spacing={2} my={1}>
                    {!values.online && (
                      <Grid item xs={12} sm={8}>
                        <LocationInput
                          name="location"
                          label={t('location')}
                          value={values.location}
                          error={locationError}
                          touched={!!touched['location']}
                          onBlur={() => {
                            setFieldTouched('location', true);
                          }}
                          onChange={(location) => {
                            setFieldValue('location', location);
                            if (location) {
                              handleLocationSelected(location);
                            }
                          }}
                          initialLocation={
                            mode === EventFormMode.CREATE
                              ? computedInitialStateForCreate.location ||
                                userData?.location
                              : undefined
                          }
                        />
                      </Grid>
                    )}

                    {values.online && (
                      <Grid item xs={12} sm={8}>
                        <TextField
                          name="location"
                          label={t('location')}
                          value=""
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.location && Boolean(errors.location)}
                          helperText={
                            touched.location && errors.location
                              ? typeof errors.location === 'string'
                                ? errors.location
                                : JSON.stringify(errors.location)
                              : ''
                          }
                          fullWidth
                          disabled
                        />
                      </Grid>
                    )}

                    <Grid
                      item
                      xs={12}
                      sm={4}
                      sx={{display: 'flex', justifyContent: 'center'}}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.online}
                            onChange={(event) => {
                              handleChange(event);
                              const newValue = event.target.checked;
                              handleFieldChange('online', newValue);
                            }}
                            name="online"
                          />
                        }
                        label={t('online_event')}
                      />
                    </Grid>
                  </Grid>
                )}
                {!onDemand && (
                  <Grid
                    container
                    spacing={2}
                    my={1}
                    marginBottom={{xs: 1, sm: 0}}
                  >
                    <Grid item xs={12} sm={3} marginBottom={{xs: 1, sm: 0}}>
                      <DatePicker
                        label={t('date')}
                        value={values.date ? new Date(values.date) : null}
                        slotProps={{
                          textField: {
                            error: errors.date ? true : false,
                            helperText:
                              touched.date && errors.date
                                ? typeof errors.date === 'string'
                                  ? errors.date
                                  : JSON.stringify(errors.date)
                                : ''
                          }
                        }}
                        onChange={(date) => {
                          const newValue = date ? date.toISOString() : '';
                          setFieldValue('date', newValue);
                          handleFieldChange('date', newValue);
                          if (!values.end && date) {
                            if (values.recurring.length > 0) {
                              const inTwoWeeks = addDays(
                                date,
                                14
                              ).toISOString();
                              setTimeout(() => {
                                setFieldValue('end', inTwoWeeks);
                                handleFieldChange('end', inTwoWeeks);
                              }, 0);
                            } else {
                              setFieldValue('end', newValue);
                              handleFieldChange('end', newValue);
                            }
                          }
                        }}
                        minDate={today}
                        sx={{
                          width: '100%',
                          mb: {xs: 1, sm: 0}
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3} marginBottom={{xs: 1, sm: 0}}>
                      <TimePicker
                        label={t('time')}
                        value={values.time ? new Date(values.time) : null}
                        slotProps={{
                          textField: {
                            error: errors.time ? true : false,
                            helperText:
                              touched.time && errors.time
                                ? typeof errors.time === 'string'
                                  ? errors.time
                                  : JSON.stringify(errors.time)
                                : ''
                          }
                        }}
                        onChange={(newTime) => {
                          const newValue = newTime ? newTime.toISOString() : '';
                          setFieldValue('time', newValue);
                          handleFieldChange('time', newValue);
                          if (!values.endTime && newTime) {
                            const newEndTime = addHours(
                              newTime,
                              1
                            ).toISOString();
                            setFieldValue('endTime', newEndTime);
                            handleFieldChange('endTime', newEndTime);
                          }
                        }}
                        disablePast={isSameDay(
                          values.date ? new Date(values.date) : new Date(),
                          today
                        )}
                        sx={{
                          width: '100%'
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={3} marginBottom={{xs: 1, sm: 0}}>
                      <DatePicker
                        label={t('end_date')}
                        value={values.end ? new Date(values.end) : null}
                        slotProps={{
                          textField: {
                            error: errors.end ? true : false,
                            helperText:
                              touched.end && errors.end
                                ? typeof errors.end === 'string'
                                  ? errors.end
                                  : JSON.stringify(errors.end)
                                : ''
                          }
                        }}
                        onChange={(endDate) => {
                          const newValue = endDate ? endDate.toISOString() : '';
                          setFieldValue('end', newValue);
                          handleFieldChange('end', newValue);
                        }}
                        minDate={values.recurring.length > 0 ? inWeek : today}
                        maxDate={getMaximumDate(values.date)}
                        sx={{
                          width: '100%',
                          mb: {xs: 1, sm: 0}
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3} marginBottom={{xs: 1, sm: 0}}>
                      <TimePicker
                        label={t('end_time')}
                        value={values.endTime ? new Date(values.endTime) : null}
                        slotProps={{
                          textField: {
                            error: errors.endTime ? true : false,
                            helperText:
                              touched.endTime && errors.endTime
                                ? typeof errors.endTime === 'string'
                                  ? errors.endTime
                                  : JSON.stringify(errors.endTime)
                                : ''
                          }
                        }}
                        onChange={(newTime) => {
                          const newValue = newTime ? newTime.toISOString() : '';
                          setFieldValue('endTime', newValue);
                          handleFieldChange('endTime', newValue);
                        }}
                        disablePast={isSameDay(
                          values.end ? new Date(values.end) : new Date(),
                          today
                        )}
                        minTime={halfHourFromStart}
                        sx={{
                          width: '100%'
                        }}
                      />
                    </Grid>
                  </Grid>
                )}
                <Grid container spacing={2} my={1}>
                  <Grid
                    item
                    xs={12}
                    sm={onDemand ? 6 : 4}
                    sx={{
                      mb: {xs: 2, sm: 0}
                    }}
                  >
                    <SportSelectInput
                      name="sports"
                      value={values.sports}
                      error={sportsError}
                      touched={!!touched.sports}
                      onBlur={handleBlur}
                      onChange={(sportId) => {
                        setFieldValue('sports', sportId);
                        handleFieldChange('sports', sportId);
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={onDemand ? 6 : 4}
                    sx={{
                      marginBottom: {xs: 2, sm: 0}
                    }}
                  >
                    <FormControl
                      sx={{width: '100%'}}
                      variant="outlined"
                      fullWidth
                    >
                      <InputLabel>{t('ageGroups')}</InputLabel>
                      <Select
                        name="ageGroups"
                        value={values.ageGroups}
                        onChange={(event) => {
                          const newValue = event.target.value as string[];

                          let updatedValue = newValue;

                          if (
                            newValue.includes('ALL') &&
                            values.ageGroups.length > 0 &&
                            !values.ageGroups.includes('ALL')
                          ) {
                            updatedValue = ['ALL'];
                          } else if (
                            newValue.length > 1 &&
                            newValue.includes('ALL')
                          ) {
                            updatedValue = newValue.filter(
                              (val) => val !== 'ALL'
                            );
                          }

                          setFieldValue('ageGroups', updatedValue);
                          handleFieldChange('ageGroups', updatedValue);
                        }}
                        onBlur={handleBlur}
                        error={touched.ageGroups && Boolean(errors.ageGroups)}
                        label="Age Category"
                        multiple
                      >
                        {ageGroupsAll.map((age) => (
                          <MenuItem key={age.value} value={age.value}>
                            {age.label}
                          </MenuItem>
                        ))}
                      </Select>

                      {touched.ageGroups && errors.ageGroups && (
                        <FormHelperText error>
                          {Array.isArray(errors.ageGroups)
                            ? errors.ageGroups.join(', ')
                            : typeof errors.ageGroups === 'string'
                            ? errors.ageGroups
                            : ''}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  {!onDemand && (
                    <Grid item xs={12} sm={4} marginBottom={{xs: 1, sm: 0}}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>
                          <FormLabel text={'max_participants'} />
                        </InputLabel>
                        <Select
                          label={<FormLabel text={'max_participants'} />}
                          value={
                            values.maxParticipants === null
                              ? 'unlimited'
                              : values.maxParticipants
                          }
                          onChange={(e) => {
                            handleChange(e);
                            const newMaxParticipants =
                              e.target.value === 'unlimited'
                                ? null
                                : Number(e.target.value);
                            handleFieldChange(
                              'maxParticipants',
                              newMaxParticipants
                            );
                          }}
                          name="maxParticipants"
                          MenuProps={{
                            PaperProps: {
                              style: {
                                minWidth: '6%'
                              }
                            }
                          }}
                        >
                          <MenuItem value={'unlimited'}>
                            {t('unlimited')}
                          </MenuItem>
                          {Array.from(
                            {
                              length:
                                99 -
                                Math.max(1, effectiveCurrentParticipants) +
                                1
                            },
                            (_, i) =>
                              i + Math.max(1, effectiveCurrentParticipants)
                          ).map((number) => (
                            <MenuItem key={number} value={number}>
                              {number}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
                {!onDemand && (
                  <Grid
                    container
                    spacing={2}
                    my={{xs: 2, sm: 3}}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FormControlLabel
                        sx={{marginLeft: {xs: 0, sm: 4}}}
                        control={
                          <Switch
                            checked={
                              Array.isArray(values.recurring) &&
                              values.recurring.length > 0
                            }
                            onChange={(event) => {
                              const newValue = event.target.checked ? [0] : [];
                              setFieldValue('recurring', newValue);
                              setFieldTouched('recurring', true, true);
                              setFieldTouched('end', true, true);
                              setFieldTouched('date', true, true);
                              if (!event.target.checked) {
                                setFieldValue('end', null);
                              }
                              handleFieldChange('recurring', newValue);
                            }}
                            name="recurring"
                          />
                        }
                        label={
                          <Box
                            sx={{display: 'flex', alignItems: 'center', gap: 2}}
                          >
                            {t('recurring')}
                            <Tooltip title={t('recurring_description')} arrow>
                              <IconButton
                                size="small"
                                sx={{
                                  display: {xs: 'flex', sm: 'none', md: 'flex'}
                                }}
                              >
                                <InfoOutlinedIcon
                                  color="primary"
                                  fontSize="small"
                                />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        }
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FormControlLabel
                        sx={{
                          marginRight: {xs: 1, sm: 0}
                        }}
                        control={
                          <Switch
                            checked={values.accessType === 'PRIVATE'}
                            onChange={(event) => {
                              const newValue = event.target.checked
                                ? 'PRIVATE'
                                : 'PUBLIC';
                              setFieldValue('accessType', newValue);
                              handleFieldChange('accessType', newValue);
                            }}
                            name="accessType"
                          />
                        }
                        label={
                          <Box
                            sx={{display: 'flex', alignItems: 'center', gap: 2}}
                          >
                            {t('private_title')}
                            <Tooltip title={t('private_description')} arrow>
                              <IconButton
                                size="small"
                                sx={{
                                  display: {xs: 'flex', sm: 'none', md: 'flex'}
                                }}
                              >
                                <InfoOutlinedIcon
                                  color="primary"
                                  fontSize="small"
                                />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        }
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center'
                      }}
                    >
                      <FormControlLabel
                        sx={{
                          marginLeft: {xs: 2, sm: 0}
                        }}
                        control={
                          <Switch
                            checked={values.isPaid}
                            onChange={(event) => {
                              handleToggleIsPaid(event.target.checked);
                            }}
                            name="isPaid"
                          />
                        }
                        label={
                          <Box sx={{display: 'flex', flexDirection: 'column'}}>
                            <span>{t('paid')}</span>
                            <ResponsiveTypography mediumAllSizes>
                              {fee}
                            </ResponsiveTypography>
                            <ResponsiveTypography mediumAllSizes>
                              {t('stripe_fee_info')}
                            </ResponsiveTypography>
                            <Link
                              sx={{
                                fontSize: {xs: '0.6rem', sm: '0.7rem'},
                                textDecoration: 'none',
                                color: theme.palette.customColors?.urlLink
                              }}
                              href={TERMS_OF_USE_URL}
                              target="_blank"
                            >
                              {t('terms_and_conditions_sj')}
                            </Link>
                          </Box>
                        }
                      />
                    </Grid>

                    {isAllowedToCreatePaidEvents && (
                      <>
                        {circularProgressVisible && (
                          <Grid item xs={12} sm={6}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                              }}
                            >
                              <CircularProgress
                                size={isSmallScreen ? 45 : 65}
                              />
                            </Box>
                          </Grid>
                        )}

                        {values.isPaid && (
                          <>
                            <Grid
                              item
                              xs={12}
                              sm={6}
                              sx={{
                                marginTop: 2,
                                display: 'flex',
                                justifyContent: 'center'
                              }}
                            >
                              <TextField
                                name="price"
                                label={
                                  recurringEventsCount > 1
                                    ? t('netto_price_recurring', {
                                        count: recurringEventsCount
                                      })
                                    : t('netto_price')
                                }
                                value={values.price ?? ''}
                                sx={{width: {xs: '100%', sm: '70%'}}}
                                onFocus={(e) => {
                                  if (e.target.value === '0') {
                                    handleChange({
                                      target: {
                                        name: 'price',
                                        value: ''
                                      }
                                    });
                                  }
                                }}
                                onChange={(e) => {
                                  const rawValue = e.target.value;
                                  let formattedValue = rawValue.replace(
                                    /[^0-9.,]/g,
                                    ''
                                  );
                                  if (formattedValue.includes(',')) {
                                    formattedValue = formattedValue.replace(
                                      ',',
                                      '.'
                                    );
                                  }
                                  if (formattedValue.includes('.')) {
                                    const [integerPart, decimalPart] =
                                      formattedValue.split('.');
                                    if (decimalPart && decimalPart.length > 2) {
                                      return;
                                    }
                                    formattedValue = `${
                                      parseInt(integerPart, 10) || 0
                                    }.${decimalPart || ''}`;
                                  } else {
                                    formattedValue = `${
                                      parseInt(formattedValue, 10) || 0
                                    }`;
                                  }

                                  const parsedValue =
                                    parseFloat(formattedValue);
                                  handleChange({
                                    target: {
                                      name: 'price',
                                      value: formattedValue
                                    }
                                  });
                                  handleFieldChange(
                                    'price',
                                    isNaN(parsedValue) ? undefined : parsedValue
                                  );
                                }}
                                onBlur={handleBlur}
                                error={touched.price && Boolean(errors.price)}
                                helperText={
                                  touched.price && errors.price
                                    ? typeof errors.price === 'string'
                                      ? errors.price
                                      : JSON.stringify(errors.price)
                                    : ''
                                }
                                type="text"
                              />
                            </Grid>

                            {values.isPaid && (
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                sx={{
                                  marginTop: 2,
                                  display: 'flex',
                                  justifyContent: 'center'
                                }}
                              >
                                <TextField
                                  name="bruttoPrice"
                                  label={
                                    recurringEventsCount > 1
                                      ? t('brutto_price_recurring', {
                                          count: recurringEventsCount
                                        })
                                      : t('brutto_price')
                                  }
                                  value={bruttoPrice}
                                  sx={{width: {xs: '100%', sm: '70%'}}}
                                  disabled
                                  type="text"
                                />
                              </Grid>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </Grid>
                )}
                {values.isPaid && (
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      alignItems: 'flex-start',
                      my: {xs: 0, sm: 2}
                    }}
                  >
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <Box sx={{width: '100%'}}>
                        <TextField
                          name="imprintLink"
                          label={t('imprint_link')}
                          value={values.imprintLink}
                          sx={{
                            width: {xs: '100%'},
                            '& .MuiFormLabel-root': {
                              fontSize: '0.9rem'
                            }
                          }}
                          onChange={(e) => {
                            handleChange(e);
                            handleFieldChange('imprintLink', e.target.value);
                          }}
                          onBlur={handleBlur}
                          error={
                            touched.imprintLink && Boolean(errors.imprintLink)
                          }
                          helperText={
                            touched.imprintLink && errors.imprintLink
                              ? typeof errors.imprintLink === 'string'
                                ? errors.imprintLink
                                : JSON.stringify(errors.imprintLink)
                              : ''
                          }
                        />
                        <Box sx={{marginTop: 1, textAlign: 'left'}}>
                          <ResponsiveTypography smallAllSizes>
                            {t('link_or_contact_with_sj')}
                          </ResponsiveTypography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <Box sx={{width: '100%'}}>
                        <TextField
                          name="termsOfUseLink"
                          label={t('terms_of_participation_link')}
                          value={values.termsOfUseLink}
                          sx={{
                            width: {xs: '100%'},
                            '& .MuiFormLabel-root': {
                              fontSize: '0.9rem'
                            }
                          }}
                          onChange={(e) => {
                            handleChange(e);
                            handleFieldChange('termsOfUseLink', e.target.value);
                          }}
                          onBlur={handleBlur}
                          error={
                            touched.termsOfUseLink &&
                            Boolean(errors.termsOfUseLink)
                          }
                          helperText={
                            touched.termsOfUseLink && errors.termsOfUseLink
                              ? typeof errors.termsOfUseLink === 'string'
                                ? errors.termsOfUseLink
                                : JSON.stringify(errors.termsOfUseLink)
                              : ''
                          }
                        />
                        <Box sx={{marginTop: 1, textAlign: 'left'}}>
                          <ResponsiveTypography smallAllSizes>
                            {t('link_or_contact_with_sj')}
                          </ResponsiveTypography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={4}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginBottom: {xs: 2, sm: 0}
                      }}
                    >
                      <TextField
                        name="cancellationPolicyLink"
                        label={<FormLabel text={'cancellation_policy_link'} />}
                        value={values.cancellationPolicyLink}
                        sx={{
                          width: {xs: '100%'},
                          '& .MuiFormLabel-root': {
                            fontSize: '0.9rem'
                          }
                        }}
                        onChange={(e) => {
                          handleChange(e);
                          handleFieldChange(
                            'cancellationPolicyLink',
                            e.target.value
                          );
                        }}
                        onBlur={handleBlur}
                        error={
                          touched.cancellationPolicyLink &&
                          Boolean(errors.cancellationPolicyLink)
                        }
                        helperText={
                          touched.cancellationPolicyLink &&
                          errors.cancellationPolicyLink
                            ? typeof errors.cancellationPolicyLink === 'string'
                              ? errors.cancellationPolicyLink
                              : JSON.stringify(errors.cancellationPolicyLink)
                            : ''
                        }
                      />
                    </Grid>
                  </Grid>
                )}
                <Grid
                  container
                  spacing={2}
                  marginBottom={3}
                  marginTop={{xs: 0, sm: 3}}
                  justifyContent={'center'}
                >
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{display: 'flex', justifyContent: 'center'}}
                  >
                    <Button
                      component="span"
                      variant="outlined"
                      color="error"
                      startIcon={<CachedIcon />}
                      onClick={handleDeleteImage}
                      sx={{
                        display: {xs: 'none', sm: 'flex'},
                        textTransform: 'none'
                      }}
                    >
                      {t('show_default_photo')}
                    </Button>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{display: 'flex', justifyContent: 'center'}}
                  >
                    <label htmlFor="photo-upload">
                      <Button
                        component="span"
                        variant="outlined"
                        color="secondary"
                        startIcon={<PhotoCamera />}
                        sx={{
                          display: {xs: 'none', sm: 'flex'},
                          textTransform: 'none'
                        }}
                      >
                        {t('upload_photo')}
                      </Button>
                    </label>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginRight: 2,
                      marginBottom: 1
                    }}
                  >
                    <label htmlFor="phote-delete-xs">
                      <IconButton
                        aria-label={t('show_default_photo')}
                        color="error"
                        onClick={handleDeleteImage}
                        component="span"
                        sx={{
                          display: {xs: 'flex', sm: 'none'}
                        }}
                      >
                        <CachedIcon />
                      </IconButton>
                    </label>

                    <input
                      id="photo-upload"
                      type="file"
                      onChange={handleImageChange}
                      accept=".jpg, .jpeg, .png"
                      hidden
                    />

                    <label htmlFor="photo-upload-xs">
                      <IconButton
                        color="secondary"
                        aria-label={t('upload_photo')}
                        component="span"
                        sx={{
                          display: {xs: 'flex', sm: 'none'},
                          marginLeft: 4,
                          marginTop: '1px'
                        }}
                      >
                        <PhotoCamera />
                      </IconButton>
                    </label>
                    <input
                      id="photo-upload-xs"
                      type="file"
                      onChange={handleImageChange}
                      accept=".jpg, .jpeg, .png"
                      hidden
                    />
                  </Grid>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    marginTop: {xs: 0, sm: 1}
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 2,
                      flexDirection: 'column'
                    }}
                  >
                    <ResponsiveTypography>
                      {t('event_photo')}
                    </ResponsiveTypography>
                    <ResponsiveTypography>
                      {t('optimal_dimensions', {width: 2, height: 1})}
                    </ResponsiveTypography>
                  </Box>
                  <Box
                    component="img"
                    src={pictureSource as string}
                    alt={t('event_photo')}
                    sx={{
                      objectFit: 'cover',
                      width: {xs: '240px', sm: '300px'},
                      height: {xs: '120px', sm: '150px'},
                      marginTop: {xs: 2, sm: 0}
                    }}
                  />
                </Grid>

                {fileSizeError && (
                  <ResponsiveTypography
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      color: 'red',
                      marginTop: 3
                    }}
                  >
                    {fileSizeError}
                  </ResponsiveTypography>
                )}
                <Grid container spacing={2} my={2} marginBottom={5}>
                  <Grid
                    item
                    xs={12}
                    sm={4}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      order: {xs: 1, sm: 0}
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        handleResetState();
                        resetForm({
                          values: initialState.eventForm
                        });
                        if (onCancel) {
                          onCancel();
                        }
                      }}
                      sx={{
                        width: {xs: '50%', sm: '80%'}
                      }}
                    >
                      {t('cancel')}
                    </Button>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={4}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: {xs: 1, sm: 0}
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        if (mode === EventFormMode.CREATE) {
                          handleResetState();
                          resetForm({
                            values: initialState.eventForm
                          });
                        } else if (mode === EventFormMode.UPDATE) {
                          resetForm();
                        } else if (mode === EventFormMode.CLONE) {
                          resetForm();
                          setFieldValue('date', null);
                          setFieldValue('time', null);
                        }
                        validateForm();

                        resetTextLength();
                      }}
                      sx={{
                        width: {xs: '50%', sm: '80%'}
                      }}
                    >
                      {t('reset')}
                    </Button>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={4}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: {xs: 1, sm: 0},
                      order: {xs: -1, sm: 0}
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        handleSubmit();
                      }}
                      sx={{
                        width: {xs: '50%', sm: '80%'}
                      }}
                    >
                      {mode === EventFormMode.UPDATE
                        ? t('update')
                        : t('create')}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            );
          }}
        </Formik>
        <ConfirmationDialog
          open={stripeVerificationDialogVisible}
          title={t('complete_onboarding_short')}
          message={
            odooCompanyCreated && !stripeAccountVerified
              ? t('stripe_account_not_verified')
              : !odooCompanyCreated && !stripeAccountVerified
              ? t('stripe_account_not_verified_no_company')
              : t('stripe_account_no_company')
          }
          yesButton={t('complete_onboarding_short')}
          onConfirm={handleStripeOnboarding}
          onCancel={handleStripeDialogClose}
        />
      </div>
      <DialogForm
        open={odooCompanyFormDialogOpen}
        onSubmit={onOdooCompanyCreated}
        onCancel={onOdooCompanyCreationCanceled}
        onError={onOdooCompanyCreationError}
        FormComponent={OdooCompanyForm}
        maxHeightAllowed="1200px"
      />
    </>
  );
};

export default EventForm;
